import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Mountain, ArrowLeft, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import StayVitals from '../components/StayVitals';
import SimilarStays from '../components/SimilarStays';
import EnquireModal from '../components/EnquireModal';
import { trackLead } from '../utils/trackLead';

const API = process.env.REACT_APP_BACKEND_URL;

const PARTNER_DATA = {
  bluehouse: {
    slug: 'bluehouse',
    tagline: 'Tirthan Valley, Himachal Pradesh',
    headline: 'Where the River Speaks',
    intro: 'A riverside sanctuary in the heart of Tirthan Valley — where the sound of water is your constant companion.',
    story: [
      'Some places don\'t just welcome you — they hold you. Bluehouse by the River is one of those rare places. Sitting right at the edge of the Tirthan River, the sound of water is your constant companion here — at breakfast, on the veranda, drifting through your window at night.',
      'Built in local stone with blue-painted doors that have become its signature, Bluehouse has been a quiet retreat since 2015. It\'s not a resort and it doesn\'t try to be one. It\'s a home by the river, with rooms that look out onto water and mountains, meals cooked with love, and a pace of life dictated by the sun and the seasons.',
      'The Great Himalayan National Park — a UNESCO World Heritage Site — begins just ten kilometres away. But many guests find they never want to leave the property at all. The river is right there. The mountains are right there. And so is the trout fishing, the bonfires under a million stars, the birdsong at dawn.',
    ],
    closingQuote: 'There are places you visit. And then there are places that stay with you long after you\'ve left.',
    photoSections: [
      {
        label: 'The Property',
        layout: 'duo',
        images: [
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/abakhhee_BH%20Exterior.jpg', caption: 'Stone & blue — built to belong here' },
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/um1c215o_BH%20Lawn.jpg', caption: 'The veranda overlooks the river below' },
        ]
      },
      {
        label: 'The River',
        layout: 'full',
        images: [
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/j1m8q6xp_IMG_2872.JPG', caption: 'Tirthan River — steps from your room' },
        ]
      },
      {
        label: 'The Table',
        layout: 'single-right',
        images: [
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/o83mw0y2_BH%20Dining.jpg', caption: 'Rustic dining with valley views — authentic Himachali and European fare' },
        ]
      },
      {
        label: 'Through the Seasons',
        layout: 'trio',
        images: [
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/v3lzzwto_IMG_2873.JPG', caption: 'Where the valley opens up' },
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/0nkkiez4_IMG_2875.JPG', caption: 'Winter transforms everything' },
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/vdtvrl1c_IMG_2874.JPG', caption: 'Moonlit nights on the river' },
        ]
      },
      {
        label: 'The Surroundings',
        layout: 'duo',
        images: [
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/9m2un9md_IMG_2871.JPG', caption: 'The entrance by the river' },
          { url: 'https://customer-assets.emergentagent.com/job_bluehouse-site/artifacts/v3lzzwto_IMG_2873.JPG', caption: 'Morning light on the Tirthan' },
        ]
      }
    ],
    testimonials: [
      'One of the nicest, most beautifully located properties in the Himalayas. Right next to the river! The rooms are clean, comfortable, well appointed and very tastefully done. The food is absolutely superb.',
      'A wonderful getaway destination. The resort offers clean, spacious rooms with breathtaking views. Exceptional service from friendly staff made our stay memorable.',
      'Staying at Bluehouse was an experience we will cherish forever. The property itself is a gem, with elegant and beautiful surroundings that took our breath away.',
    ],
    stats: { established: '2015', rooms: '5', altitude: '1,400m', rating: '4.4/5' },
    startingPrice: '6,000',
    whatsapp: '919891355963',
    whatsappMessage: "Hi, I found Bluehouse by the River on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
  },
  eaglenest: {
    slug: 'eaglenest',
    tagline: 'Tirthan Valley, Himachal Pradesh',
    headline: 'Above the Clouds',
    intro: 'A stone homestead at 2,000 metres — perched on the edge of a forest, overlooking the entire Tirthan Valley.',
    story: [
      'Perched on the edge of a forest-covered mountain, six hundred metres above the Tirthan River, Eagle\'s Nest is the kind of place you don\'t stumble upon — you climb to. And when you arrive, you understand why.',
      'The house is built in local stones, in the middle of apple orchards. There are only three bedrooms — this isn\'t a hotel, it\'s someone\'s home that they\'ve opened to travellers who understand the value of silence, good books, and the right view at the right time of day.',
      'From the balcony, you can see the entire Tirthan Valley stretching out below — from the Shrikhand Mahadev snow-capped peak to the Beas River in the distance. The Great Himalayan National Park sits right in front of you. There\'s a library, a Spanish guitar, a digital piano, and board games for the evenings. The meals are a mix of Himachali and European home-cooking.',
      'At two thousand metres, you\'re not just visiting the mountains — you\'re living in them.',
    ],
    closingQuote: 'Some homes are built with stone. Others are built with intention. This one was built with both.',
    photoSections: [
      {
        label: 'The Homestead',
        layout: 'duo',
        images: [
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2022/06/tirthan-valley-stay-eagle-nest-107.jpg', caption: 'Stone walls under a Himalayan winter' },
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2020/01/tirthan-valley-stay-eagle-nest-95.jpg', caption: 'The homestead and the mountains beyond' },
        ]
      },
      {
        label: 'The Rooms',
        layout: 'duo',
        images: [
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2017/05/tirthan-valley-stay-eagle-nest-01.jpg', caption: 'The Sunset Valley room — stone, wood, and mountain light' },
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2019/10/tirthan-valley-stay-eagle-nest-89.jpg', caption: 'The Sunrise room — waking up inside the mountains' },
        ]
      },
      {
        label: 'The Table & Living',
        layout: 'duo',
        images: [
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2017/05/tirthan-valley-stay-eagle-nest-12.jpg', caption: 'Dining with the Great Himalayan National Park in view' },
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2022/06/tirthan-valley-stay-eagle-nest-108.jpg', caption: 'The piano corner — evenings are different here' },
        ]
      },
      {
        label: 'The Valley Below',
        layout: 'full',
        images: [
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2017/05/tirthan-valley-stay-eagle-nest-26.jpg', caption: 'Sunset paints the valley in gold' },
        ]
      },
      {
        label: 'Through the Seasons',
        layout: 'trio',
        images: [
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2017/05/tirthan-valley-stay-eagle-nest-42.jpg', caption: 'Snow-covered peaks in winter' },
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2017/05/tirthan-valley-stay-eagle-nest-31.jpg', caption: 'Apple blossoms in spring' },
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2022/06/ten-03.jpg', caption: 'Sunset from the garden' },
        ]
      },
      {
        label: 'The Landscape',
        layout: 'duo',
        images: [
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2017/05/tirthan-valley-stay-eagle-nest-28.jpg', caption: 'The Great Himalayan National Park' },
          { url: 'https://tirthan-stay-eagle-nest.com/wp-content/uploads/2017/05/tirthan-valley-stay-eagle-nest-37.jpg', caption: 'From the rooftop — the world opens up' },
        ]
      }
    ],
    testimonials: [
      'A haven of peace in a unique environment. You wake up to birdsong in dense Deodar forest and end your day watching the sunset paint the Great Himalayan National Park in gold.',
      'The house is built with such care and intention. Three rooms, a piano, a library — everything you need and nothing you don\'t. The views from the balcony are simply unforgettable.',
      'We didn\'t want to leave. The home-cooked meals, the silence, the apple orchards — it felt like stepping into a different world entirely.',
    ],
    stats: { established: '2018', rooms: '2', altitude: '2,000m', rating: '4.8/5' },
    startingPrice: '5,500',
    whatsapp: '919816423344',
    whatsappMessage: "Hi, I found Eagle's Nest on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
  },
  tra: {
    slug: 'tra',
    tagline: 'Kullu Valley, Himachal Pradesh',
    headline: 'A Wooded Cottage in the Orchards',
    intro: 'A chalet-style boutique stay amidst apple orchards — where pine wood, Bohemian soul, and the Pir Pranjal Range meet.',
    story: [
      'Trea is a Swedish word that translates to a wooded cottage. And that is exactly what Tra is — a warm, pine-scented home nestled in apple orchards on the Naggar-Manali road, twenty minutes from town but a world away from it. Five rooms, each with private balconies that open onto snowcapped peaks.',
      'The two Attic Suites are fully wooded, with dormer windows that frame a hundred-and-eighty-degree view of the Pir Pranjal Range — right from your bed. The three Luxury Rooms are semi-wooded, with the same mountain-facing balconies and the same feeling of waking up inside the Himalayas.',
      'The kitchen garden feeds the table. The menu travels — Indian, Italian, Bhutanese, Continental — but always lands on soulful. Between meals, there are momo-making workshops, Himachali Dhaam dinner experiences, nature walks to trails nobody has mapped, and day trips into the Trans Himalayas. Tra is pet friendly, eco-conscious, and built with intention — not a hotel, but a home opened to travellers who understand that the best stays are the ones that feel like they were made just for you.',
    ],
    closingQuote: 'I know a little cottage where everything is just right.',
    photoSections: [
      {
        label: 'The Cottage',
        layout: 'duo',
        images: [
          { url: 'https://static.wixstatic.com/media/9140e1_6b54b0636cd84886af1d501f1692d32f~mv2.jpg/v1/fill/w_740,h_460,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/9140e1_6b54b0636cd84886af1d501f1692d32f~mv2.jpg', caption: 'Stone and green — a winter wonderland' },
          { url: 'https://static.wixstatic.com/media/9140e1_203ece5421be4786a07b10f4ddcce43c~mv2.jpg/v1/fill/w_740,h_569,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/9140e1_203ece5421be4786a07b10f4ddcce43c~mv2.jpg', caption: 'Amidst apple orchards on the Naggar-Manali road' },
        ]
      },
      {
        label: 'The Attic Suites',
        layout: 'duo',
        images: [
          { url: 'https://static.wixstatic.com/media/9140e1_a836fed079aa46e6a019fc5fab2fba0b~mv2.jpg/v1/fill/w_740,h_481,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/9140e1_a836fed079aa46e6a019fc5fab2fba0b~mv2.jpg', caption: 'Fully wooded interiors — the Pir Pranjal through dormer windows' },
          { url: 'https://static.wixstatic.com/media/9140e1_6ff902522a60447d8e7b8c32022981ba~mv2.jpeg/v1/fill/w_740,h_555,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/9140e1_6ff902522a60447d8e7b8c32022981ba~mv2.jpeg', caption: 'Pine, stone, and mountain light' },
        ]
      },
      {
        label: 'The Luxury Rooms',
        layout: 'single-right',
        images: [
          { url: 'https://static.wixstatic.com/media/9140e1_42e5d3fba6e84cfea132f57438a6ddd6~mv2.jpg/v1/fill/w_740,h_555,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/9140e1_42e5d3fba6e84cfea132f57438a6ddd6~mv2.jpg', caption: 'Private balcony views that stretch to the horizon — purple chairs, mountain panorama, nowhere to be' },
        ]
      },
      {
        label: 'The Living',
        layout: 'single-right',
        images: [
          { url: 'https://static.wixstatic.com/media/9140e1_a36c73b4845b4e83aafe7483d16b2089~mv2.jpg/v1/fill/w_740,h_525,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/9140e1_a36c73b4845b4e83aafe7483d16b2089~mv2.jpg', caption: 'Bohemian corners, floor cushions, and Chitta the resident cat' },
        ]
      },
      {
        label: 'The Table',
        layout: 'single-right',
        images: [
          { url: 'https://static.wixstatic.com/media/9140e1_0a9eb9aaa5af41d0b9ed21875640e251~mv2.jpg/v1/fill/w_740,h_840,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/9140e1_0a9eb9aaa5af41d0b9ed21875640e251~mv2.jpg', caption: 'Kitchen garden to table — Indian, Italian, Bhutanese, Continental' },
        ]
      },
    ],
    testimonials: [
      'A hidden gem on the Naggar road. The rooms are stunning, the food is incredible, and the hosts make you feel like family. We didn\'t want to leave.',
      'The attic suite with the dormer window view of Pir Pranjal is something you have to experience to believe. Waking up to that every morning changed something in us.',
      'Pet friendly, soulful food, momo-making workshops — Tra is not just a stay, it\'s an experience. Our dog loved the trails as much as we did.',
    ],
    stats: { established: '2020', rooms: '5', altitude: '1,800m', rating: '4.9/5' },
    startingPrice: '5,500',
    whatsapp: '918851657804',
    whatsappMessage: "Hi, I found Tra Boutique Stay on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
  },
  homestead: {
    slug: 'homestead',
    tagline: 'Sasbani, Nainital, Uttarakhand',
    headline: 'Where the Forest Feeds the Kiln',
    intro: 'An eco-friendly homestead between two oak ridges — where a baker and a potter built a life in the mountains.',
    story: [
      'Some places are discovered by accident and never forgotten. Himalayan Homestead sits in village Sasbani — a name you won\'t find on most maps — cradled between two oak ridges in the Nainital district. The drive in takes you past Mukteshwar, down a narrow road through a canopy of oak trees, until you arrive at a cobbled path that leads, improbably, to a collection of five cottages built with stone, mud-plastered walls, and wooden floors — each one named for the feeling it gives you: Nirvana, Divinity, Bliss, Serenity, Joyful.',
      'This is the dream made real by a baker and a potter — two people who left the city to build a life in the mountains. The pottery studio sits at the heart of the homestead, where clay is turned by hand into vessels that hold the spirit of this place. The baking studio fills the mornings with the aroma of freshly baked sourdough, cinnamon rolls, and artisan breads that you eat overlooking snowcapped peaks — Trishul, Nandadevi, Nandakot — stretched across the horizon like a painting that never changes and is never the same twice.',
      'The food is entirely vegetarian, drawn from the kitchen garden and neighbouring village farms. There are no menus — only what the season offers. Meals are shared at a long communal table by the fireplace, the way meals were always meant to be shared. Between meals, there are forest walks through oak thickets alive with birdsong, or you simply sit on a wooden deck with a book and a cup of chamomile tea, watching the light change across five Himalayan peaks.',
    ],
    closingQuote: 'Some homes are found at the end of cobbled paths, between two oak ridges, where a baker and a potter decided to stay.',
    photoSections: [
      {
        label: 'The Homestead',
        layout: 'duo',
        images: [
          { url: 'https://himalayanhomestead.in/images/gallery/img_1.jpg', caption: 'Turquoise rooftops between oak ridges — Sasbani from above' },
          { url: 'https://himalayanhomestead.in/images/gallery/img_4.jpg', caption: 'Stone, stucco, and the terraced hillside behind' },
        ]
      },
      {
        label: 'The Cottages',
        layout: 'duo',
        images: [
          { url: 'https://himalayanhomestead.in/images/gallery/nirvanaf.jpg', caption: 'Nirvana — an independent cottage next to a brook' },
          { url: 'https://himalayanhomestead.in/images/gallery/blissf.jpg', caption: 'Bliss at nightfall — warm light through the trees' },
        ]
      },
      {
        label: 'The Valley',
        layout: 'full',
        images: [
          { url: 'https://himalayanhomestead.in/images/gallery/img_3.jpg', caption: 'Rolling green hills and trails that lead into the oak forest' },
        ]
      },
      {
        label: 'The Table',
        layout: 'single-right',
        images: [
          { url: 'https://himalayanhomestead.in/images/gallery/g2.jpg', caption: 'Freshly baked bread from the studio — the kitchen garden feeds the table' },
        ]
      },
      {
        label: 'The Studio',
        layout: 'duo',
        images: [
          { url: 'https://himalayanhomestead.in/images/gallery/g5.jpg', caption: 'Handcrafted ceramics drying in the mountain air' },
          { url: 'https://himalayanhomestead.in/images/gallery/g7.jpg', caption: 'From kiln to shelf — each piece holds the spirit of this place' },
        ]
      },
      {
        label: 'The Surroundings',
        layout: 'duo',
        images: [
          { url: 'https://himalayanhomestead.in/images/gallery/img_6.jpg', caption: 'Morning light on the balcony — five peaks on the horizon' },
          { url: 'https://himalayanhomestead.in/images/gallery/serenity1.jpg', caption: 'Serenity cottage — tucked into the forest edge' },
        ]
      }
    ],
    testimonials: [
      'Such a peaceful space which has been beautifully designed. Rooms are spacious and cosy and balcony and open spaces are a big plus. We could have stayed here for weeks. Food was delicious and loved all the homemade breads, cakes and jams.',
      'A decision on a whim led us to this thoughtfully conceived, beautifully curated and joyfully delivered homestead, hidden in an enchanted Oak Forest. Everything seems magical about this place.',
      'From the first cup of tea on the balcony that warms one\'s very insides to the last cup of chamomile cinnamon tea — I am running out of words to describe the utterly beautiful experience I\'ve had.',
    ],
    stats: { established: '2021', rooms: '5', altitude: '1,800m', rating: '4.7/5' },
    startingPrice: '5,500',
    whatsapp: '918445439374',
    whatsappMessage: "Hi, I found Himalayan Homestead on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
    hostsSection: {
      label: 'From Kiln to Table',
      intro: 'A homestead built by two people who traded the city for clay and flour — and never looked back.',
      makers: [
        {
          title: 'The Potter',
          description: 'Yogesh shapes clay the way the mountains shape the sky — slowly, with intention. The pottery studio at the heart of the homestead is where earth becomes art. Each vessel fired in the kiln carries the quiet of this valley, the texture of its soil, the patience of hands that have learned to listen to the wheel.',
          image: { url: 'https://himalayanhomestead.in/images/gallery/g6.jpg', caption: 'Handcrafted ceramics drying under the Himalayan sky' },
        },
        {
          title: 'The Baker',
          description: 'Namita bakes the way the forest grows — seasonally, instinctively, with whatever the land offers. The baking studio fills the mornings with the aroma of sourdough and cinnamon, of herb-crusted focaccia and fruit preserves made from the orchard. No recipes are repeated. Every loaf is a conversation with the day.',
          image: { url: 'https://himalayanhomestead.in/images/gallery/g9.jpg', caption: 'Rosemary rolls fresh from the wood-fired oven' },
        }
      ],
      gallery: [
        { url: 'https://himalayanhomestead.in/images/gallery/g1.jpg', caption: 'Morning bakes — warm from the studio' },
        { url: 'https://himalayanhomestead.in/images/gallery/g3.jpg', caption: 'Star bread for the winter table' },
        { url: 'https://himalayanhomestead.in/images/gallery/g8.jpg', caption: 'Strawberry cream from the kitchen garden' },
      ]
    }
  },
  rajucottage: {
    slug: 'rajucottage',
    partnerType: 'valley',
    tagline: 'Tirthan Valley, Himachal Pradesh',
    headline: 'Across the River, Into the Wild',
    intro: 'A family-run riverside cottage in Gushaini — reached by hand-pulled ropeway over the Tirthan River — where the food is legendary, the orchards are endless, and the mountains never let you forget where you are.',
    story: [
      'There are places in the Himalayas you drive to. And then there are places you have to cross a river to reach. Raju Bharti\'s Cottage is the latter — accessible only by a hand-pulled ropeway over the rushing, turquoise waters of the Tirthan. That minute of swaying above the river, mountains on every side, sets the tone for everything that follows.',
      'The Bharti family has been welcoming travellers here for over two decades. Raju, the patriarch, built the first rooms with his own hands. His sons Karan and Varun now run the place with the same warmth and directness their father is known for. Eight rooms across two wooden cottages — simple, clean, with balconies that open onto the river and the forested valley beyond. No televisions. No pretence. Just the sound of water and wind through deodar.',
      'What draws people back, year after year, is the food. Lata ji\'s kitchen serves home-cooked Himachali meals that have become the stuff of legend — fresh trout pulled from the river that morning, seasonal vegetables from the garden, rajma-chawal that tastes different at a thousand metres. Between meals, there are apple and cherry orchards to wander through, trout pools to cast a line into, forest trails that lead to waterfalls, and bonfires under some of the darkest, most star-filled skies in Himachal Pradesh.',
      'Rated 4.7 on TripAdvisor with over 175 reviews and ranked number one in Banjar, this is not a luxury retreat. It\'s something rarer — a place that hasn\'t changed because it never needed to. The ropeway still sways. The river still roars. And Lata ji is still in the kitchen.',
    ],
    closingQuote: 'Some places you visit. Others you cross a river to find. Those are the ones you never forget.',
    photoSections: [
      {
        label: 'The Cottage',
        layout: 'duo',
        images: [
          { url: '/raju_varun_exterior.jpg', caption: 'Where wood meets mountain — the cottage rises from the valley floor, surrounded by marigold and apple' },
          { url: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/14/db/43/01/raju-bharti-s-guest-house.jpg?w=1200&h=-1&s=1', caption: 'The old block — stone, timber, and twenty years of Himalayan weather' },
        ]
      },
      {
        label: 'The River',
        layout: 'full',
        images: [
          { url: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/23/6d/5b/9a/raju-bharti-s-guest-house.jpg?w=1200&h=-1&s=1', caption: 'The Tirthan rushes past the wooden bridge — on the other side, the cottage waits between the trees' },
        ]
      },
      {
        label: 'The Rooms',
        layout: 'duo',
        images: [
          { url: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/04/80/77/76/raju-bharti-s-guest-house.jpg?w=1200&h=-1&s=1', caption: 'All wood, all warmth — the rooms smell of deodar and feel like a cabin in the clouds' },
          { url: '/raju_leena_cottage.jpg', caption: 'The common room — pine bookshelves, homemade preserves, and daylight through the forest' },
        ]
      },
      {
        label: 'The Kitchen',
        layout: 'full',
        images: [
          { url: '/raju_dining.jpg', caption: 'Lata ji\'s kitchen serves the kind of food that TripAdvisor legends are made of — trout, rajma-chawal, and rhododendron juice' },
        ]
      },
      {
        label: 'The Experience',
        layout: 'trio',
        images: [
          { url: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1b/5c/a9/90/img-20200322-204431-020.jpg?w=1200&h=-1&s=1', caption: 'Long exposure on the Tirthan — where time flows as slowly as you want it to' },
          { url: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/0e/dd/f3/64/raju-bharti-s-guest-house.jpg?w=1200&h=-1&s=1', caption: 'The cottage dogs claim their spot on the river rocks every afternoon' },
          { url: '/raju_swati_cottage.jpg', caption: 'Vine-covered rooftops and clouds that drift between the peaks' },
        ]
      },
      {
        label: 'The Landscape',
        layout: 'duo',
        images: [
          { url: 'https://dynamic-media-cdn.tripadvisor.com/media/photo-o/04/80/77/82/raju-bharti-s-guest-house.jpg?w=1200&h=-1&s=1', caption: 'The valley from above — river, road, forest, and the cottages tucked into the green' },
          { url: '/raju_tirthan.jpg', caption: 'The turquoise Tirthan carves through boulder and cliff — the wildest river in Kullu' },
        ]
      },
    ],
    testimonials: [
      'One of the best experiences of our lives! The food prepared by Lata ji is simply out of this world. Raju ji and his sons are very helpful and always have a solution for everything. The property is a paradise surrounded by cherry and apple trees.',
      'A gem of a place. We reached by ropeway over the Tirthan river which was amazing in itself. The food was delicious, home-grown and home-cooked. Trout fishing in the river right below the cottage was the highlight.',
      'We\'ve been coming here for years and it never disappoints. The cottages are simple but clean, the views are spectacular, and the warmth of the Bharti family makes you feel like you\'re visiting old friends. The bonfire under the stars is unforgettable.',
    ],
    stats: { established: '2003', rooms: '8', altitude: '1,000m', rating: '4.7/5' },
    startingPrice: '3,500',
    whatsapp: '919495833124',
    whatsappMessage: "Hi, I found Raju Bharti's Cottage on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
  },
  browndipper: {
    slug: 'browndipper',
    partnerType: 'valley',
    tagline: 'Tirthan Valley, Himachal Pradesh',
    headline: 'Where the River Bird Walks Underwater',
    byline: 'by Humming Woods',
    intro: 'A timber-and-stone riverside retreat in village Jawal — named after the Brown Dipper, the stubborn little bird that walks along Himalayan streambeds, underwater, against the current.',
    story: [
      'The Brown Dipper is a small, dark bird that lives along fast-flowing Himalayan streams. It doesn\'t fly over the water — it walks into it, against the current, underwater, foraging along the riverbed as if gravity and physics are suggestions. It\'s the kind of creature that makes you question what\'s possible.',
      'Brown Dipper House carries that spirit. Built on the banks of the Tirthan River in village Jawal by Naresh, the property glows at night like a lantern in the forest — warm timber, warm light, the sound of the river constant and close. Five rooms across three categories — Deluxe, Standard, and Superior — each with stone walls, wooden floors, and balconies that open directly onto the river and the deodar-covered slopes beyond.',
      'The veranda is where evenings happen. String lights strung across wooden beams, the river rushing below, mountains darkening against the last light, and the kind of quiet conversation that only happens when there\'s no television and no Wi-Fi worth fighting for. Naresh has been building this place since 2014 — room by room, beam by beam — always with the river as the centrepiece.',
      'The Great Himalayan National Park is a short drive away. Jalori Pass is an hour up the mountain. But most guests find the real attraction is right here — the morning birdsong, the trout pools, the forest trails that start from the doorstep, and the bonfire nights under some of the darkest skies in Himachal. This is not a resort with a river view. This is a river with a house beside it.',
    ],
    closingQuote: 'Named after a bird that walks underwater. Built for people who want to go against the current.',
    photoSections: [
      {
        label: 'The House',
        layout: 'duo',
        images: [
          { url: 'https://hummingwoods.in/images/uploads/403/77161__8_.png', caption: 'Timber and stone rising from the hillside — the cottage at dusk' },
          { url: 'https://hummingwoods.in/images/uploads/403/64171__1_.png', caption: 'Wooden balconies and forest canopy, built into the slope above the river' },
        ]
      },
      {
        label: 'The River',
        layout: 'full',
        images: [
          { url: 'https://hummingwoods.in/images/uploads/403/80621__2_.png', caption: 'The Tirthan from the balcony — turquoise water, deodar forest, and the sound that never stops' },
        ]
      },
      {
        label: 'The Rooms',
        layout: 'duo',
        images: [
          { url: 'https://hummingwoods.in/images/uploads/403/39711__2_.png', caption: 'Stone walls, warm light — the Standard rooms feel carved from the mountain itself' },
          { url: 'https://a0.muscache.com/im/pictures/hosting/Hosting-14271153/original/c2308196-90fa-4ba8-9134-f43857089b48.jpeg?im_w=720', caption: 'The Super Deluxe — three-side panoramic windows, wooden floors, and the river always in earshot' },
        ]
      },
      {
        label: 'The Veranda',
        layout: 'duo',
        images: [
          { url: 'https://hummingwoods.in/images/uploads/403/2451__3_.png', caption: 'String lights, stone pillars, and the river below — evenings at Brown Dipper' },
          { url: 'https://hummingwoods.in/images/uploads/403/70631__7_.png', caption: 'The dining veranda at night — where the mountains disappear and the stars take over' },
        ]
      },
      {
        label: 'The View',
        layout: 'full',
        images: [
          { url: 'https://a0.muscache.com/im/pictures/09458794-2512-4e04-8443-4f3637a70eba.jpg?im_w=720', caption: 'The house from across the river — green bridge, forest slopes, and the Tirthan carving its path through the valley' },
        ]
      },
      {
        label: 'The Dining',
        layout: 'duo',
        images: [
          { url: 'https://a0.muscache.com/im/pictures/hosting/Hosting-14271153/original/954bea74-2806-44d2-88d2-2afda534fd22.jpeg?im_w=720', caption: 'Morning tea on the balcony — the river below, the mountains ahead, nowhere to be' },
          { url: 'https://a0.muscache.com/im/pictures/356e894d-d562-4ceb-97f2-95ad25b87d8c.jpg?im_w=720', caption: 'The outdoor dining table at night — stone patio, warm light, and the forest pressing in' },
        ]
      },
    ],
    testimonials: [
      'We stayed at Brown Dipper House for three nights and didn\'t want to leave. The rooms are simple but beautifully done — stone walls, wooden floors, and the sound of the river all night. Naresh is a wonderful host.',
      'The night-time view of this place is magical — it literally glows in the forest. The veranda with string lights and the river rushing below is the most peaceful dinner setting we\'ve ever had.',
      'Perfect base for Tirthan Valley. We did the GHNP trek, fished for trout, and spent evenings by the bonfire. The food was home-cooked and delicious. Will come back every year.',
    ],
    stats: { established: '2014', rooms: '5', altitude: '1,400m', rating: '4.5/5' },
    startingPrice: '3,500',
    whatsapp: '919050266563',
    whatsappMessage: "Hi, I found Brown Dipper House on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
  },
  villaaavaas: {
    slug: 'villaaavaas',
    partnerType: 'featured',
    tagline: 'Kullu Valley, Himachal Pradesh',
    headline: 'The Quiet Side of Kullu-Manali',
    byline: 'hosted by Bobby',
    intro: 'A four-bedroom mountain villa in Nangabagh, Raison — thirty kilometres south of Manali, where the Beas Valley opens wide and the highway noise fades into orchard silence.',
    story: [
      'Thirty kilometres south of Manali, where the highway noise fades and the Beas Valley opens into a wide, green calm, Villa Aavaas sits in the village of Nangabagh — tucked just below the main road yet a world apart from it. Bobby, a Superhost with ten years of mountain hospitality behind him, built this four-bedroom villa for the kind of traveller who wants Kullu-Manali without the crowd.',
      'The villa is designed around its views. A large private terrace on the first floor faces the Beas river valley and the mountains beyond — sunrise yoga sessions, morning coffee, or simply watching the light move across the peaks. Two rooms sit on the ground floor, two upstairs, each with ensuite bathrooms, queen beds, and the kind of quiet that only comes from being surrounded by orchards and open sky.',
      'The living room is generous — sofa seating, a dining table, a fireplace for winter evenings, and a fully equipped kitchen where Bobby\'s team prepares home-cooked meals on request. The garden outside is lush and private — ideal for evening hangouts, small celebrations, or simply sitting under the stars with the sound of the river in the distance.',
      'Raison is the quieter sibling of Manali — close enough for day trips to Solang, Rohtang, and Old Manali, but far enough that the traffic and tourist bustle never reach the doorstep. The Beas runs wide and clean here, the orchards are thick with apple and plum, and the mountains fill every window. Villa Aavaas is for families, friend groups, and remote workers who want comfort with a view — and a host who knows exactly how to deliver both.',
    ],
    closingQuote: 'The quiet side of Kullu-Manali — where the Beas runs wide, the orchards run deep, and the mountains fill every window.',
    photoSections: [
      {
        label: 'The Villa',
        layout: 'duo',
        images: [
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/511e5382-7580-4cbc-9415-a1e91c405e63.png', caption: 'Modern stone-and-white facade against the Kullu Valley mountains' },
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/048f3110-4c14-468d-afc6-7e2baeec0bd2.png', caption: 'Another angle of the villa — multi-story comfort built into the hillside' },
        ]
      },
      {
        label: 'The Valley',
        layout: 'full',
        images: [
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/c4f43f41-9d8c-466a-ad7e-e374623ec72c.png', caption: 'The Beas Valley from the terrace — river, orchards, and snow peaks in one unbroken sweep' },
        ]
      },
      {
        label: 'The Rooms',
        layout: 'ensemble',
        images: [
          { url: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1403026613847635750/original/7274e768-527d-4e29-a52b-d033a00009bc.jpeg', caption: 'Double-height living room — stone fireplace, exposed beams, green velvet' },
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/e962d5c2-0cd0-4f0c-abcd-f122f5ba0893.png', caption: 'The lounge — stone walls, warm light, wooden staircase' },
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/b9c6e8f8-2c35-457f-86ce-6a170c76daf0.png', caption: 'Dining area with fireplace — where evenings happen' },
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/70d58809-af64-4f5e-a199-029e4579b9b2.png', caption: 'Bedroom under wooden rafters — tufted headboard, mountain quiet' },
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/bb31db18-e0e6-4c9a-bb6e-0ffe838670f3.png', caption: 'Queen room with valley views through the curtains' },
        ]
      },
      {
        label: 'The View',
        layout: 'duo',
        images: [
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/cdffe984-7017-45cb-9f07-6d942edd09f4.png', caption: 'Valley panorama — village rooftops, apple orchards, and the peaks beyond' },
          { url: 'https://a0.muscache.com/im/pictures/miso/Hosting-1403026613847635750/original/62b5dd0b-28c2-47af-9bdb-8e1dd0925c54.png', caption: 'The garden through the doorway — green sofas, warm stone, and the mountains beyond' },
        ]
      },
    ],
    testimonials: [
      'Bobby is the best host we have had in any mountain stay. The villa is spacious, clean, and the views from the terrace are absolutely stunning. We did not want to leave.',
      'Perfect for families — four bedrooms, a big living room with a fireplace, and a garden where the kids ran around all day. The home-cooked meals were incredible.',
      'We came for three days and stayed for a week. Raison is the quiet Manali nobody talks about — apple orchards, the Beas River, and Villa Aavaas right in the middle of it all.',
    ],
    stats: { hosting: '10 years', rooms: '4', altitude: '1,200m', rating: '4.7/5' },
    startingPrice: '14,000',
    whatsapp: '919882259876',
    whatsappMessage: "Hi, I found Villa Aavaas on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
  },
  srirangam: {
    slug: 'srirangam',
    partnerType: 'featured',
    tagline: 'Shantiniketan, West Bengal',
    headline: 'An Arena of Beauty in Tagore\'s Town',
    byline: 'hosted by Shumon & Ananya',
    intro: 'An art-filled villa-style homestay in the UNESCO World Heritage buffer zone of Shantiniketan — where every wall tells a story in paint, stone, and collected memory.',
    story: [
      'Shantiniketan was built by Rabindranath Tagore as a place where art and learning would breathe freely under open skies. A century later, Srirangam Homestay carries that same spirit — an independent, art-filled villa in the UNESCO World Heritage buffer zone, where every wall, every corner, every stairwell tells a story in paint, stone, and collected memory.',
      'Six ensuite bedrooms are arranged across two floors, each distinguished by curated artworks, exposed brick, and a design vocabulary rooted in India\'s cultural history. The common living and dining area opens onto a garden with water features and lush planting. Below the house, the Srinidhi Art Gallery holds masks from Africa, sculptures from across the subcontinent, and paintings gathered over decades of travel.',
      'Shumon and Ananya built Srirangam as a homage to the aesthetic spirit of Shantiniketan — a place where art is not decoration but daily life. Meals are home-cooked Bengali fare: luchi and shaak, slow-simmered curries, and mishti served with the kind of warmth that turns strangers into family.',
      'Srijani Shilpagram is two minutes away. Sonajhuri Forest, the Rabindra Museum, and the famous Saturday haat at Khoai — the red laterite landscape that has defined Shantiniketan\'s visual identity for a century — are all within easy reach. Featured in Condé Nast Traveller and rated a perfect 5.0 on Google, Srirangam is not a stay. It is an experience.',
    ],
    closingQuote: 'Srirangam in Sanskrit means an arena of beauty and wellbeing. The name fits.',
    photoSections: [
      {
        label: 'The Property',
        layout: 'duo',
        images: [
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/IMG_7984-805x453.jpg', caption: 'Garden courtyard with water features, brick facade, and lush planting' },
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/Srirangam-Home-10-805x453.jpg', caption: 'The villa rising through palm trees — brick, laterite, and Bengal sky' },
        ]
      },
      {
        label: 'The Rooms',
        layout: 'ensemble',
        images: [
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/09/IMG_E7920-805x453.jpg', caption: 'Room Aadi — cane bed, brick walls, botanical prints, and Bengali blue linen' },
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/IMG_3486-805x453.jpg', caption: 'The dining area — art on brick, colourful tablecloth, and daybed seating' },
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/IMG_7721-805x453.jpg', caption: 'Living room — sofas, curated art, and garden light through the window' },
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/IMG_7798-805x453.jpg', caption: 'The lounge — daybed, African masks, coffee table, and a Buddha in the corner' },
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/IMG_3611-805x453.jpg', caption: 'Books, brick, and a lifetime of collected art — the reading corner' },
        ]
      },
      {
        label: 'The Garden',
        layout: 'full',
        images: [
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/Srirangam-Frontyard-7-805x453.jpg', caption: 'Red soil path and green canopy — the front garden at Srirangam' },
        ]
      },
      {
        label: 'After Dark',
        layout: 'duo',
        images: [
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/IMG_3252-805x453.jpg', caption: 'The villa at dusk — warm light spilling through arched alcoves' },
          { url: 'https://srirangamhomestay.net/wp-content/uploads/2024/12/IMG_4027-805x453.jpg', caption: 'Brick balcony with ironwork railing and the garden pressing close' },
        ]
      },
    ],
    testimonials: [
      'This was truly one of the loveliest stays I\'ve had. It\'s rare to find a guest house where the soul is so palpable, infused into the very walls of the house, in each inch of detail.',
      'From the art deco rooms to the warm hosts to the beautiful, simple food, the collection of art, books and artefacts — everything stood as a concise testament to what Shantiniketan stands for.',
      'This place has featured in Condé Nast Traveller. The owners have eclectic taste. All rooms have a touch of art. The entire house has a touch of art and culture. Must visit!',
    ],
    stats: { rooms: '6', rating: '5.0/5', reviews: '40+', press: 'Condé Nast' },
    startingPrice: '6,000',
    whatsapp: '918944932312',
    whatsappMessage: "Hi, I found Srirangam Homestay on GoHimalayan and would love to book a stay. Could you help me with availability and rates?",
  }
};

function CollectiveWatermark({ size = 'sm' }) {
  const isLg = size === 'lg';
  return (
    <div className="inline-flex items-center">
      <div className={`relative flex items-center bg-[#3a5a40]/80 backdrop-blur-sm ${isLg ? 'px-5 py-2.5 rounded-lg' : 'px-3 py-1.5 rounded-md'}`}>
        <span className={`absolute right-2 font-bold text-white/10 leading-none select-none ${isLg ? 'text-5xl' : 'text-4xl'}`} style={{letterSpacing: '-2px'}}>100</span>
        <svg className={`${isLg ? 'w-5 h-5 mr-2' : 'w-4 h-4 mr-1.5'} text-white/90 flex-shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        </svg>
        <div className="relative z-10 flex flex-col leading-tight">
          <span className={`${isLg ? 'text-xs' : 'text-[10px]'} font-semibold text-white tracking-wide`} style={{fontFamily: "'Playfair Display', Georgia, serif"}}>GoHimalayan</span>
          <span className={`${isLg ? 'text-[9px]' : 'text-[7px]'} font-medium text-white/70 tracking-[0.2em] uppercase`}>Collective</span>
        </div>
      </div>
    </div>
  );
}

function ValleyPartnerWatermark({ size = 'sm' }) {
  const isLg = size === 'lg';
  return (
    <div className="inline-flex items-center">
      <div className={`flex items-center gap-1.5 bg-white/90 backdrop-blur-sm shadow-sm border border-stone-100 ${isLg ? 'px-4 py-2.5 rounded-lg' : 'px-2.5 py-1.5 rounded-md'}`}>
        <svg className={`${isLg ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-[#3a5a40] flex-shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        </svg>
        <span className={`${isLg ? 'text-xs' : 'text-[9px]'} font-semibold text-stone-500 tracking-[0.12em] uppercase`} style={{fontFamily: 'Georgia, serif'}}>Valley Partner</span>
      </div>
    </div>
  );
}

function FeaturedWatermark({ size = 'sm' }) {
  const isLg = size === 'lg';
  return (
    <div className="inline-flex items-center">
      <div className={`flex items-center gap-1.5 bg-white/90 backdrop-blur-sm shadow-sm border border-stone-100 ${isLg ? 'px-4 py-2.5 rounded-lg' : 'px-2.5 py-1.5 rounded-md'}`} style={{boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
        <svg className={`${isLg ? 'w-4 h-4' : 'w-3 h-3'} text-[#2C5F8D] flex-shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        </svg>
        <span className={`${isLg ? 'text-xs' : 'text-[9px]'} font-medium text-stone-500 tracking-[0.12em] uppercase`} style={{fontFamily: 'Georgia, serif'}}>Featured</span>
      </div>
    </div>
  );
}

function PartnerBadge({ partnerType, size = 'sm' }) {
  if (partnerType === 'featured') return <FeaturedWatermark size={size} />;
  if (partnerType === 'valley') return <ValleyPartnerWatermark size={size} />;
  return <CollectiveWatermark size={size} />;
}

function PhotoSection({ section }) {
  const { label, layout, images } = section;
  
  if (layout === 'full') {
    return (
      <div className="my-16 md:my-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-5 font-medium">{label}</p>
        </div>
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative group overflow-hidden rounded-lg">
            <img src={images[0].url} alt={images[0].caption} className="w-full h-[50vh] md:h-[65vh] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <p className="absolute bottom-6 left-8 text-white/90 text-sm italic opacity-0 group-hover:opacity-100 transition-all duration-700 translate-y-3 group-hover:translate-y-0" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{images[0].caption}</p>
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'single-right') {
    return (
      <div className="my-16 md:my-24 max-w-6xl mx-auto px-6 md:px-12">
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-5 font-medium">{label}</p>
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <p className="md:w-1/3 text-stone-500 italic text-base leading-relaxed" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            {images[0].caption}
          </p>
          <div className="md:w-2/3 group overflow-hidden rounded-lg">
            <img src={images[0].url} alt={images[0].caption} className="w-full h-[300px] md:h-[380px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'trio') {
    return (
      <div className="my-16 md:my-24 max-w-6xl mx-auto px-6 md:px-12">
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-5 font-medium">{label}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {images.map((img, i) => (
            <div key={i} className="group relative overflow-hidden rounded-lg">
              <img src={img.url} alt={img.caption} className="w-full h-[280px] md:h-[320px] object-cover transition-transform duration-1000 group-hover:scale-[1.03] img-enhance" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <p className="absolute bottom-4 left-5 right-5 text-white/90 text-xs italic opacity-0 group-hover:opacity-100 transition-all duration-700 translate-y-2 group-hover:translate-y-0" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{img.caption}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (layout === 'ensemble') {
    // Bento-box mosaic: 1 tall hero left, 4 smaller tiles right
    return (
      <div className="my-16 md:my-24 max-w-6xl mx-auto px-6 md:px-12">
        <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-5 font-medium">{label}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3" style={{gridTemplateRows: 'auto auto'}}>
          {/* Hero image — spans 2 rows on desktop, full width on mobile */}
          <div className="group relative overflow-hidden rounded-lg col-span-2 md:col-span-1 md:row-span-2">
            <img src={images[0].url} alt={images[0].caption} className="w-full h-[220px] md:h-full object-cover transition-transform duration-1000 group-hover:scale-[1.03] img-enhance" style={{minHeight: '100%'}} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <p className="absolute bottom-4 left-5 right-5 text-white/90 text-xs italic opacity-0 group-hover:opacity-100 transition-all duration-700 translate-y-2 group-hover:translate-y-0" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{images[0].caption}</p>
          </div>
          {/* 4 smaller tiles */}
          {images.slice(1, 5).map((img, i) => (
            <div key={i} className="group relative overflow-hidden rounded-lg">
              <img src={img.url} alt={img.caption} className="w-full h-[140px] md:h-[185px] object-cover transition-transform duration-1000 group-hover:scale-[1.05] img-enhance" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <p className="absolute bottom-3 left-4 right-4 text-white/90 text-[10px] italic opacity-0 group-hover:opacity-100 transition-all duration-700 translate-y-2 group-hover:translate-y-0" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{img.caption}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default: duo layout
  return (
    <div className="my-16 md:my-24 max-w-6xl mx-auto px-6 md:px-12">
      <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-5 font-medium">{label}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {images.map((img, i) => (
          <div key={i} className="group relative overflow-hidden rounded-lg">
            <img src={img.url} alt={img.caption} className="w-full h-[300px] md:h-[380px] object-cover transition-transform duration-1000 group-hover:scale-[1.03] img-enhance" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <p className="absolute bottom-5 left-6 right-6 text-white/90 text-sm italic opacity-0 group-hover:opacity-100 transition-all duration-700 translate-y-2 group-hover:translate-y-0" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{img.caption}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


function HostsSection({ data }) {
  if (!data) return null;
  const { label, intro, makers, gallery } = data;

  return (
    <section className="my-20 md:my-28" data-testid="hosts-section">
      {/* Section Header */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 mb-16">
        <div className="max-w-3xl">
          <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">{label}</p>
          <p className="text-stone-500 text-xl md:text-2xl italic leading-relaxed" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            {intro}
          </p>
        </div>
      </div>

      {/* Two Makers — asymmetric editorial layout */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 space-y-20">
        {makers.map((maker, i) => (
          <div key={i} className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-12 items-center`}>
            <div className="md:w-1/2 group overflow-hidden rounded-lg">
              <img
                src={maker.image.url}
                alt={maker.image.caption}
                className="w-full h-[320px] md:h-[420px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance"
              />
            </div>
            <div className="md:w-1/2 flex flex-col justify-center">
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#3a5a40] mb-4 font-semibold">{maker.title}</p>
              <p className="text-stone-600 text-base md:text-lg leading-[1.9] italic" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                {maker.description}
              </p>
              <p className="text-stone-400 text-xs italic mt-6" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                {maker.image.caption}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Craft Gallery — trio */}
      {gallery && gallery.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 md:px-12 mt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {gallery.map((img, i) => (
              <div key={i} className="group relative overflow-hidden rounded-lg">
                <img src={img.url} alt={img.caption} className="w-full h-[260px] md:h-[300px] object-cover transition-transform duration-1000 group-hover:scale-[1.03] img-enhance" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <p className="absolute bottom-4 left-5 right-5 text-white/90 text-xs italic opacity-0 group-hover:opacity-100 transition-all duration-700 translate-y-2 group-hover:translate-y-0" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{img.caption}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}



export default function CollectiveStay() {
  const { slug } = useParams();
  const [apiData, setApiData] = useState(null);
  const [enquireOpen, setEnquireOpen] = useState(false);

  const content = PARTNER_DATA[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    async function fetchData() {
      try {
        const res = await fetch(`${API}/api/curated/partner/${slug}`);
        if (res.ok) setApiData(await res.json());
      } catch (e) { console.error(e); }
    }
    fetchData();
  }, [slug]);

  // Fallback path — the slug isn't in PARTNER_DATA (a new partner stay was
  // added on the backend without hand-authored content). Send the user to the
  // canonical /stay/:slug detail page. One property, one detail page.
  if (!content) {
    return <Navigate to={`/stay/${slug}`} replace />;
  }

  const defaultNames = { bluehouse: 'Bluehouse by the River', eaglenest: "Eagle's Nest", tra: 'Tra Boutique Stay', homestead: 'Himalayan Homestead', rajucottage: "Raju Bharti's Cottage", browndipper: 'Brown Dipper House', villaaavaas: 'Villa Aavaas', srirangam: 'Srirangam Homestay' };
  const { headline, byline, tagline, intro, story, closingQuote, photoSections, testimonials, stats, startingPrice, whatsapp, whatsappMessage, hostsSection } = content;
  const displayName = apiData?.display_name || defaultNames[slug] || headline;
  const heroImage = (apiData?.images || []).find(i => i?.section === 'hero')?.url
    || apiData?.images?.[0]?.url
    || photoSections[0]?.images[0]?.url;

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="collective-stay-page">
      <SEOHead
        title={`${displayName} — GoHimalayan Collective 100`}
        description={intro}
        image={heroImage}
        url={`/collective/${slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          "name": displayName,
          "description": intro,
          "image": heroImage,
          "url": `https://gohimalayan.com/collective/${slug}`,
          "address": {
            "@type": "PostalAddress",
            "addressRegion": tagline,
            "addressCountry": "IN"
          }
        }}
      />
      {/* Google Font */}
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&display=swap" rel="stylesheet" />

      {/* Sticky Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#faf8f4]/90 backdrop-blur-md border-b border-stone-200/40">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
          <Link to="/discovered" className="flex items-center gap-2 text-stone-500 hover:text-stone-800 transition-colors" data-testid="back-to-discovered">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs tracking-wider uppercase">Back</span>
          </Link>
          <PartnerBadge partnerType={content.partnerType} />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[90vh] min-h-[640px]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={heroImage} alt={displayName} className="w-full h-full object-cover img-enhance" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/15" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end max-w-6xl mx-auto px-6 md:px-12 pb-20">
          <p className="text-white/50 text-[11px] tracking-[0.4em] uppercase mb-5">{tagline}</p>
          <h1 className="text-3xl sm:text-5xl lg:text-7xl text-white tracking-tight mb-4 font-light italic" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            {headline}
          </h1>
          {byline && (
            <p className="text-white/40 text-base tracking-wide italic mb-4" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>{displayName} {byline}</p>
          )}
          <p className="text-white/90 text-base md:text-lg max-w-xl font-light" style={{fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 1px 8px rgba(0,0,0,0.5)'}}>
            {intro}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 md:gap-10 mt-8 md:mt-10 text-white/70 text-xs md:text-sm tracking-[0.15em] uppercase font-medium">
            {/* Slimmed: only social-proof stats here (est. + rating).
                Room counts, altitude and starting price live in the dramatic
                <StayVitals> strip below to avoid duplication. */}
            {Object.entries(stats).map(([key, val]) => {
              if (key === 'established') return <span key={key}>Est. {val}</span>;
              if (key === 'reviews') return <span key={key}>{val} reviews</span>;
              if (key === 'press') return <span key={key}>{val}</span>;
              if (key === 'rating' || key === 'rated') return <span key={key}>{val} rated</span>;
              return null;
            })}
          </div>
        </div>
      </section>

      {/* White panel container */}
      <div className="bg-white mx-4 md:mx-8 lg:mx-16 -mt-8 relative z-20 rounded-t-2xl shadow-[0_-8px_40px_rgba(0,0,0,0.06)]" data-testid="content-panel">

        {/* Stay Vitals — at-a-glance dramatic fact strip */}
        <StayVitals stay={apiData} />

        {/* Story */}
        <section className="max-w-3xl mx-auto px-6 md:px-12 pt-20 pb-16 text-center" data-testid="story-section">
          <div className="w-12 h-[1px] bg-stone-300 mb-12 mx-auto" />
          <div>
            {story.map((para, i) => (
              <div key={i}>
                {i > 0 && <div className="w-16 h-[1px] bg-stone-200 mx-auto my-10" />}
                <p className="text-stone-600 text-base md:text-xl leading-[1.9] font-light italic" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                  {para}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="w-full h-[1px] bg-stone-200" />
        </div>

        {/* Photo Sections */}
        {photoSections.map((section, i) => (
          <PhotoSection key={i} section={section} />
        ))}

        {/* Meet the Hosts (optional — only for properties with hostsSection data) */}
        {hostsSection && (
          <>
            <div className="max-w-3xl mx-auto px-6 md:px-12">
              <div className="w-full h-[1px] bg-stone-200" />
            </div>
            <HostsSection data={hostsSection} />
          </>
        )}

        {/* Closing Quote */}
        <section className="max-w-3xl mx-auto px-6 md:px-12 py-20">
          <div className="w-12 h-[1px] bg-stone-300 mb-12" />
          <blockquote className="text-2xl md:text-3xl text-stone-700 italic leading-relaxed" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            "{closingQuote}"
          </blockquote>
        </section>

        {/* Testimonials */}
        {testimonials && testimonials.length > 0 && (
          <section className="bg-[#faf8f4] py-16 px-6 md:px-12" data-testid="testimonial-section">
            <div className="max-w-3xl mx-auto">
              <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-10 font-medium">What Guests Say</p>
              <div>
                {testimonials.map((quote, i) => (
                  <div key={i}>
                    {i > 0 && <div className="w-16 h-[1px] bg-stone-300/60 mx-auto my-10" />}
                    <p className="text-stone-600 text-lg italic leading-relaxed" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                      "{quote}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="bg-[#2C3E2D] py-20 px-6 md:px-12 text-center rounded-b-2xl" data-testid="cta-section">
          <div className="max-w-xl mx-auto">
            <div className="flex justify-center mb-8">
              <PartnerBadge partnerType={content.partnerType} size="lg" />
            </div>
            <h2 className="text-3xl md:text-4xl text-white font-light italic tracking-tight mb-3" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
              Stay at {displayName.split(',')[0]}
            </h2>
            <p className="text-white/50 text-sm mb-10 max-w-sm mx-auto">
              Your enquiry goes directly to {displayName.split(',')[0]}. GoHimalayan connects you with the property for a seamless booking experience.
            </p>
            {startingPrice && (
              <div className="mb-8" data-testid="cta-price">
                <p className="text-white/40 text-xs mb-1.5 tracking-wider uppercase">Starting from</p>
                <p className="text-white text-2xl md:text-3xl font-light italic tracking-tight" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                  &#8377;{startingPrice}<span className="text-white/50 text-base font-normal"> /room/night</span>
                </p>
                <p className="text-white/40 text-xs mt-1 tracking-wider uppercase">with breakfast</p>
              </div>
            )}
            <a
              href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                // All Collective stays now use the guided EnquireModal
                // (structured dates / guests / rooms → cleanly-labelled WA message to host).
                e.preventDefault();
                setEnquireOpen(true);
              }}
              data-testid="whatsapp-cta"
              className="inline-flex items-center gap-3 bg-white text-[#2C3E2D] px-10 py-4 rounded-full text-sm font-medium hover:bg-white/90 transition-all hover:shadow-lg"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Check dates & enquire
            </a>
          </div>
        </section>
      </div>

      {/* Similar stays — "Stays you might also love" */}
      <SimilarStays currentStay={apiData} />

      {/* Footer spacing */}
      <div className="bg-[#faf8f4] py-10 text-center">
        <Link to="/" className="text-stone-400 text-xs tracking-wider hover:text-stone-600 transition-colors uppercase">
          GoHimalayan &middot; Curated Himalayan Experiences
        </Link>
      </div>

      {/* Guided EnquireModal — live on all 8 Collective stays */}
      <EnquireModal
        open={enquireOpen}
        onClose={() => setEnquireOpen(false)}
        stay={apiData || { display_name: displayName, valley: content?.tagline?.split(',')[0] }}
        whatsappNumber={whatsapp}
        startingPrice={startingPrice}
        heroImage={heroImage}
        source="collective"
        contextLabel="enquire_modal_bottom_cta"
        propertyShortName={displayName}
      />
    </div>
  );
}
